// Player detail, Phase 2 (contracts/phase2.md §12): the Pro card with the admin-only
// support grant and its revocation, XP / level / title, badges, friends and the
// reports filed against the player.

import { useState } from "react";
import { Link } from "react-router";
import { useBadges, useGrantPro, useRevokeProGrant } from "../api/queries";
import type { PlayerDetail } from "../api/schemas";
import { useCan } from "../auth/AuthContext";
import { Medallion } from "../components/Medallion";
import { Button, Card, Chip, ConfirmDialog, Dialog, Field, InlineError, Stat, TextInput } from "../components/ui";
import { formatDateTime } from "../lib/dates";
import { fmtInt } from "../lib/format";
import { PLAN_LABEL, PRODUCT_LABEL } from "../lib/pro";
import { StripeLink } from "./payments/components";
import styles from "./pages.module.css";

export const REASON_LABEL: Record<string, string> = { handle: "Handle", cheating: "Cheating", other: "Other" };

export function PlayerPhase2({ detail }: { detail: PlayerDetail }) {
  const { pro, entitlements, xp, badges, friendsCount, reports, moderationHold } = detail;
  if (!pro && !xp && !badges.length && friendsCount === null && !reports.length) {
    return (
      <p className={styles.phaseNote} data-testid="phase2-missing">
        Pro, XP, badges, friends and reports appear here once the API serves contracts/phase2.md §12.
      </p>
    );
  }
  return (
    <div className={styles.playerPhase2}>
      <ProCard userId={detail.player.userId} pro={pro} entitlements={entitlements} />
      <XpCard xp={xp} friends={friendsCount} />
      <BadgesCard badges={badges} />
      <ReportsCard reports={reports} hold={moderationHold} />
    </div>
  );
}

// ---------------------------------------------------------------- Pro

const SOURCE_LABEL: Record<string, string> = {
  appstore: "App Store (StoreKit 2)",
  storekit: "App Store (StoreKit 2)",
  stripe: "Stripe (bought on the web)",
  grant: "support grant",
};

/** The source pill on each entitlement row (contracts/web.md §5.7: Stripe rows next to the App Store ones). */
const SOURCE_PILL: Record<string, { label: string; tone: string }> = {
  appstore: { label: "App Store", tone: "info" },
  storekit: { label: "App Store", tone: "info" },
  stripe: { label: "Stripe", tone: "accent" },
  grant: { label: "Grant", tone: "violet" },
};

type EntitlementRow = PlayerDetail["entitlements"][number];

function entitlementStatus(e: EntitlementRow): { label: string; tone: string } {
  if (e.revokedAt) return { label: e.reason === "refund" ? "refunded" : e.reason === "dispute" ? "disputed" : "revoked", tone: "danger" };
  if (e.expiresAt === null) return { label: "active", tone: "success" };
  if (Date.parse(e.expiresAt) > Date.now()) return e.inTrial ? { label: "trial", tone: "violet" } : { label: "active", tone: "success" };
  return { label: "ended", tone: "muted" };
}

function entitlementText(e: EntitlementRow): string {
  if (e.source === "grant") return `${PLAN_LABEL.grant}${e.reason ? ` · ${e.reason}` : ""}`;
  if (e.source === "stripe") return `${e.plan ? PLAN_LABEL[e.plan] : "Pro"} · web`;
  return PRODUCT_LABEL[e.productId ?? ""] ?? e.productId ?? "subscription";
}

function ProCard({ userId, pro, entitlements }: { userId: string; pro: PlayerDetail["pro"]; entitlements: PlayerDetail["entitlements"] }) {
  const canGrant = useCan("pro.grant");
  const [granting, setGranting] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const revoke = useRevokeProGrant();
  const bought = Boolean(pro && (pro.productId || pro.source === "stripe"));
  const status = !pro ? null : pro.active ? (pro.inTrial ? "trial" : "active") : bought ? (pro.plan === "lifetime" ? "refunded" : "lapsed") : "free";
  const lifetime = pro?.plan === "lifetime";
  return (
    <Card
      title="IQ Daily Pro"
      actions={
        <>
          {status === "active" || status === "trial" ? (
            <Chip tone="violet">{status === "trial" ? "trial" : lifetime ? "lifetime" : "pro"}</Chip>
          ) : status === "lapsed" || status === "refunded" ? (
            <Chip tone="warning">{status}</Chip>
          ) : (
            <Chip tone="muted">free</Chip>
          )}
          {canGrant ? (
            <Button size="sm" variant="secondary" onClick={() => setGranting(true)}>
              Grant Pro
            </Button>
          ) : null}
        </>
      }
    >
      <dl className={styles.kv} data-testid="pro-card">
        <dt>Plan</dt>
        <dd>
          {pro?.productId ? (PRODUCT_LABEL[pro.productId] ?? <span className="mono">{pro.productId}</span>) : pro?.plan ? PLAN_LABEL[pro.plan] : "—"}
          {lifetime ? <span className={styles.faint}> · one-time</span> : null}
          {pro?.source === "stripe" ? <span className={styles.faint}> · web</span> : null}
        </dd>
        {lifetime ? (
          <>
            <dt>{pro?.active ? "Expires" : "Revoked"}</dt>
            <dd className="mono" data-testid="pro-expiry">
              {pro?.active ? "never" : "refunded or revoked"}
            </dd>
          </>
        ) : (
          <>
            <dt>{pro?.active ? (pro.willRenew ? "Renews" : "Ends") : "Expired"}</dt>
            <dd className="mono" data-testid="pro-expiry">
              {pro?.expiresAt ? formatDateTime(pro.expiresAt) : "—"}
              {pro?.active && pro.source !== "grant" && (pro.productId || pro.source === "stripe") ? (
                <span className={styles.faint}>{pro.willRenew ? " · auto-renew on" : " · won't renew"}</span>
              ) : null}
            </dd>
          </>
        )}
        <dt>Source</dt>
        <dd data-testid="pro-source">{pro?.source ? (SOURCE_LABEL[pro.source] ?? pro.source) : "—"}</dd>
        {pro?.manage === "stripe" ? (
          <>
            <dt>Manage</dt>
            <dd>Stripe billing portal, from iqdaily.app</dd>
          </>
        ) : null}
      </dl>
      {pro?.active && pro.redundantSubscription ? (
        <p className={styles.footNote} data-testid="pro-redundant">
          Lifetime is active but a subscription still renews. The player keeps being charged until they cancel it in Settings › Apple ID ›
          Subscriptions; the app suggests this too.
        </p>
      ) : null}
      {pro?.grant ? (
        <div className={styles.grantBox} data-testid="pro-grant">
          <div className={styles.grantHead}>
            <span className="monoLabel">Support grant · {pro.grant.days} days</span>
            {canGrant ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  revoke.reset();
                  setRevoking(true);
                }}
              >
                Revoke
              </Button>
            ) : null}
          </div>
          <p className={styles.grantReason}>“{pro.grant.reason}”</p>
          <span className={styles.faint}>
            {pro.grant.grantedBy ?? "an admin"}
            {pro.grant.grantedAt ? ` · ${formatDateTime(pro.grant.grantedAt)}` : ""}
            {pro.grant.expiresAt ? ` · until ${formatDateTime(pro.grant.expiresAt)}` : ""}
          </span>
        </div>
      ) : null}
      {entitlements.length ? (
        <ul className={styles.entitlements} data-testid="entitlements" aria-label="Entitlement history">
          {entitlements.slice(0, 8).map((e) => {
            const pill = SOURCE_PILL[e.source] ?? { label: e.source, tone: "neutral" };
            const state = entitlementStatus(e);
            return (
              <li key={e.id} data-revoked={e.revokedAt ? true : undefined} data-source={e.source} data-testid="entitlement">
                <Chip tone={pill.tone}>{pill.label}</Chip>
                <span className={styles.entitlementText} title={e.externalId ?? undefined}>
                  {entitlementText(e)}
                </span>
                <span className={styles.entitlementMeta}>
                  <Chip tone={state.tone}>{state.label}</Chip>
                  <span className={`mono ${styles.faint}`}>
                    {e.revokedAt
                      ? `revoked ${formatDateTime(e.revokedAt)}`
                      : e.expiresAt === null
                        ? `bought ${formatDateTime(e.createdAt)} · never expires`
                        : `${Date.parse(e.expiresAt) > Date.now() ? (e.source === "stripe" && e.willRenew ? "renews" : "until") : "ended"} ${formatDateTime(e.expiresAt)}`}
                  </span>
                  <StripeLink href={e.stripeUrl} />
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
      {!canGrant ? <p className={styles.footNote}>Support grants need the admin role.</p> : null}
      <GrantDialog open={granting} userId={userId} onClose={() => setGranting(false)} />
      <ConfirmDialog
        open={revoking}
        title="Revoke this support grant?"
        confirmLabel="Revoke grant"
        tone="danger"
        busy={revoke.isPending}
        error={revoke.error}
        onCancel={() => setRevoking(false)}
        onConfirm={() => revoke.mutate(userId, { onSuccess: () => setRevoking(false) })}
      >
        <p className={styles.prose}>
          The player loses Pro now unless an App Store subscription or a lifetime purchase is also active. The revocation is written to the
          audit log.
        </p>
      </ConfirmDialog>
    </Card>
  );
}

const PRESETS = [7, 30, 90, 365];

function GrantDialog({ open, userId, onClose }: { open: boolean; userId: string; onClose: () => void }) {
  const [days, setDays] = useState("30");
  const [reason, setReason] = useState("");
  const grant = useGrantPro();
  const n = Number(days);
  const validDays = Number.isInteger(n) && n >= 1 && n <= 3650;
  const valid = validDays && reason.trim().length > 0 && reason.trim().length <= 500;
  const close = () => {
    grant.reset();
    onClose();
  };
  return (
    <Dialog
      open={open}
      title="Grant IQ Daily Pro"
      onClose={close}
      width={480}
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={grant.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!valid}
            busy={grant.isPending}
            onClick={() =>
              grant.mutate(
                { id: userId, days: n, reason: reason.trim() },
                {
                  onSuccess: () => {
                    setReason("");
                    onClose();
                  },
                },
              )
            }
          >
            Grant {validDays ? `${n} days` : "Pro"}
          </Button>
        </>
      }
    >
      <p className={styles.prose}>
        A support grant is stored as an entitlement with <code>source=grant</code>. It is always time-limited (lifetime is only sold through
        the App Store), runs alongside any App Store subscription or lifetime purchase, and is audited.
      </p>
      <div className={styles.grantForm}>
        <Field label="Days" hint="1–3650">
          <TextInput
            type="number"
            min={1}
            max={3650}
            value={days}
            onChange={(e) => setDays(e.target.value)}
            aria-invalid={!validDays || undefined}
            data-autofocus
            style={{ width: 110 }}
          />
        </Field>
        <div className={styles.presets} role="group" aria-label="Day presets">
          {PRESETS.map((p) => (
            <Button key={p} size="sm" variant={n === p ? "primary" : "ghost"} onClick={() => setDays(String(p))}>
              {p}d
            </Button>
          ))}
        </div>
      </div>
      <Field label="Reason" hint="Required · shown in the audit log">
        <textarea
          className={styles.textarea}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          rows={3}
          placeholder="e.g. Charged twice, refund pending with Apple"
        />
      </Field>
      <InlineError error={grant.error} />
    </Dialog>
  );
}

// ---------------------------------------------------------------- XP

function XpCard({ xp, friends }: { xp: PlayerDetail["xp"]; friends: number | null }) {
  return (
    <Card title="XP and level" actions={friends !== null ? <Chip tone="info">{`${friends} friend${friends === 1 ? "" : "s"}`}</Chip> : null}>
      {!xp ? (
        <p className={styles.faint}>No XP reported.</p>
      ) : (
        <div className={styles.xp} data-testid="xp-card">
          <div className={styles.xpHead}>
            <span className={styles.xpLevel}>L{xp.level}</span>
            <span className={styles.xpTitle}>{xp.title}</span>
          </div>
          <span className={styles.meter} aria-label={`${xp.into} of ${xp.needed} XP to level ${xp.level + 1}`} role="img">
            <span style={{ width: `${Math.min(100, (xp.into / Math.max(1, xp.needed)) * 100)}%` }} />
          </span>
          <div className={styles.xpFoot}>
            <span className="mono">
              {fmtInt(xp.into)} / {fmtInt(xp.needed)} to L{xp.level + 1}
            </span>
          </div>
          <div className={styles.statsRow}>
            <Stat label="Total XP" value={fmtInt(xp.total)} />
            <Stat label="This week" value={`+${fmtInt(xp.week)}`} />
            <Stat label="Friends" value={<span data-testid="friends-count">{friends === null ? "—" : fmtInt(friends)}</span>} />
          </div>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------- badges

function BadgesCard({ badges }: { badges: PlayerDetail["badges"] }) {
  // The API may send only ids and dates; names and rarity come from the catalogue.
  const catalogue = useBadges();
  const byId = new Map((catalogue.data?.badges ?? []).map((b) => [b.id, b]));
  return (
    <Card title="Badges" actions={<Chip tone="muted">{`${badges.length} / 24`}</Chip>}>
      {badges.length === 0 ? (
        <p className={styles.faint}>No badges yet.</p>
      ) : (
        <ul className={styles.badgeList} data-testid="player-badges">
          {badges.map((b) => {
            const def = byId.get(b.id);
            const name = b.name ?? def?.name ?? b.id;
            const rarity = b.rarity ?? def?.rarity ?? null;
            return (
              <li key={b.id} title={[rarity, b.earnedAt ? `earned ${formatDateTime(b.earnedAt)}` : null].filter(Boolean).join(" · ") || undefined}>
                <Medallion id={b.id} rarity={rarity} size={34} label={`${name} medallion`} />
                <span className={styles.badgeName}>{name}</span>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------- reports

function ReportsCard({ reports, hold }: { reports: PlayerDetail["reports"]; hold: boolean }) {
  const open = reports.filter((r) => r.status === "open").length;
  return (
    <Card
      title="Reports against"
      actions={
        <>
          {hold ? (
            <Chip tone="muted" title="Held off the global leaderboards">
              board hold
            </Chip>
          ) : null}
          {open ? <Chip tone="danger">{`${open} open`}</Chip> : <Chip tone="success">none open</Chip>}
          <Link to="/reports" className={styles.back}>
            Queue →
          </Link>
        </>
      }
    >
      {reports.length === 0 ? (
        <p className={styles.faint}>Nobody has reported this player.</p>
      ) : (
        <ul className={styles.reportList} data-testid="player-reports">
          {reports.map((r) => (
            <li key={r.id}>
              <div className={styles.reportHead}>
                <Chip tone={r.reason === "cheating" ? "danger" : r.reason === "handle" ? "warning" : "neutral"}>{REASON_LABEL[r.reason] ?? r.reason}</Chip>
                <Chip tone={r.status === "open" ? "info" : "muted"}>
                  {r.status === "open" ? "open" : r.action && r.action !== "none" ? r.action.replace(/_/g, " ") : r.status}
                </Chip>
                <span className={`mono ${styles.faint}`}>{formatDateTime(r.createdAt)}</span>
              </div>
              {r.note ? <span className={styles.reportNote}>{r.note}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
